import {
  FlightSearchSelectors,
  FlightShopSelectors,
} from "@b2bportal/core-flights";
import { HtsAirCfarOffersSelectors } from "@b2bportal/core-flights-cfar-offers";
import {
  getCalendarDayDifference,
  removeTimezone,
} from "@hopper-b2b/utilities";
import { TripCategory } from "@overrides/types";
import type { RootState } from "@/store";
import { isExchangeFlow } from "@/utilities/flights/isExchangeFlow";

/**
 * Why a flight review could not show the stays up-sell. Independent of the
 * experiment variant, so control traffic reports the same eligibility as the
 * treatment legs and the experiment can be read on eligible trips only.
 */
export enum UpSellIneligibleReason {
  exchange_flow = "exchange_flow",
  not_round_trip = "not_round_trip",
  /** The package only carries the flight, so the CFAR would be dropped (RBC-2570). */
  cfar_selected = "cfar_selected",
  missing_flight_data = "missing_flight_data",
  los_under_one_night = "los_under_one_night",
}

export type UpSellEligibilityInputs = {
  isExchange: boolean;
  tripCategory: TripCategory | undefined;
  returnDate: string | null | undefined;
  cfarSelected: boolean;
  /** Arrival of the selected outbound slice; undefined when the flight is not loaded. */
  outboundArrival: string | undefined;
};

/** Single set of rules behind the up-sell entry points and its tracking. */
export const getUpSellIneligibleReason = ({
  isExchange,
  tripCategory,
  returnDate,
  cfarSelected,
  outboundArrival,
}: UpSellEligibilityInputs): UpSellIneligibleReason | null => {
  if (isExchange) return UpSellIneligibleReason.exchange_flow;
  if (tripCategory !== TripCategory.ROUND_TRIP) {
    return UpSellIneligibleReason.not_round_trip;
  }
  if (cfarSelected) return UpSellIneligibleReason.cfar_selected;
  if (!outboundArrival || !returnDate) {
    return UpSellIneligibleReason.missing_flight_data;
  }
  // The stay starts when the outbound lands, not when it departs: a redeye or
  // long-haul flight can land on the return date and leave no hotel night.
  // `arrival` is local to the landing airport; removeTimezone keeps that
  // local date instead of shifting it into the browser's timezone.
  if (
    getCalendarDayDifference(removeTimezone(outboundArrival), returnDate) < 1
  ) {
    return UpSellIneligibleReason.los_under_one_night;
  }
  return null;
};

type CfarOffersState = Parameters<
  typeof HtsAirCfarOffersSelectors.selectCfarOfferWithSelectedStatus
>[0];

// The flights flow injects the CFAR slice lazily, so RootState does not type
// it and it is absent until that flow mounts.
const hasCfarSlice = (state: RootState): state is RootState & CfarOffersState =>
  "htsAirCfarOffers" in state;

const hasCfarSelected = (state: RootState): boolean =>
  hasCfarSlice(state) &&
  HtsAirCfarOffersSelectors.selectCfarOfferWithSelectedStatus(state) != null;

/** Store-backed eligibility for event transformers, which have no hooks. */
export const selectUpSellIneligibleReason = (
  state: RootState,
  isExchange: boolean = isExchangeFlow()
): UpSellIneligibleReason | null => {
  const { tripId, outgoingFareId, returnFareId, outgoingSliceId } =
    FlightShopSelectors.selectedTripSelector(state);
  const flightsData = FlightShopSelectors.getFlightsData(state);
  const outboundSlice =
    outgoingSliceId != null
      ? flightsData?.slices?.[outgoingSliceId]
      : undefined;
  const hasFlight = !!tripId && !!(returnFareId ?? outgoingFareId);

  return getUpSellIneligibleReason({
    isExchange,
    tripCategory: FlightSearchSelectors.getTripCategory(state),
    returnDate: FlightSearchSelectors.getReturnDate(state),
    cfarSelected: hasCfarSelected(state),
    outboundArrival: hasFlight ? outboundSlice?.arrival : undefined,
  });
};

export type UpSellEligibilityTrackingProperties = {
  up_sell_eligible: boolean;
  up_sell_ineligible_reason?: UpSellIneligibleReason;
};

export const getUpSellEligibilityTrackingProperties = (
  state: RootState
): UpSellEligibilityTrackingProperties => {
  const reason = selectUpSellIneligibleReason(state);
  return reason == null
    ? { up_sell_eligible: true }
    : { up_sell_eligible: false, up_sell_ineligible_reason: reason };
};
